import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFasting } from './hooks/useFasting';
import { PROTOCOLS, MOTIVATION_QUOTES, TRASH_TALK_QUOTES, BADGES } from './utils/data';
import { TimerCircle } from './components/TimerCircle';
import { MetabolicTracker } from './components/MetabolicTracker';
import { ProtocolSelector } from './components/ProtocolSelector';
import { BodyTracker } from './components/BodyTracker';
import { StatsChart } from './components/StatsChart';
import { ShareAchievements } from './components/ShareAchievements';
import { Button } from './components/ui/Button';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { shareProgress, generateFastingText, generateGoalText } from './utils/share';
import { requestNotificationPermission } from './utils/notifications';
import { trackNavigation, trackFastingEvents, trackMotivationEvents, trackAppEvents } from './utils/analytics';
import { format } from 'date-fns';

const { FiClock, FiBarChart2, FiZap, FiActivity, FiDownload, FiHeart, FiFrown, FiPlay, FiShare2, FiBell, FiSettings, FiCheck, FiCalendar } = FiIcons;

function App() {
  const {
    isFasting,
    elapsed,
    startFast,
    endFast,
    cancelFast,
    protocolId,
    targetHours,
    history,
    weightLogs,
    measurements,
    photos,
    streak,
    longestFast,
    settings,
    addWeight,
    addMeasurements,
    addPhoto,
    toggleUnits,
    updateSettings,
    startTime
  } = useFasting();

  const [activeTab, setActiveTab] = useState('timer');
  const [selectedProtocol, setSelectedProtocol] = useState(protocolId);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [activeMessage, setActiveMessage] = useState({ text: '', type: null });
  const [usedQuotes, setUsedQuotes] = useState({ motivation: [], trash: [] });
  const [isLoading, setIsLoading] = useState(true);

  // Immediate render - remove artificial delay
  useEffect(() => {
    // Set loading to false immediately
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Fix for Safari mobile height issues
    const updateHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);
    updateHeight();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
    };
  }, []);

  const handleToggleNotifications = useCallback(async () => {
    if (!settings.notificationsEnabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        updateSettings({ notificationsEnabled: true });
        trackAppEvents.notificationsEnabled();
      } else {
        alert("Please enable notification permissions in your browser settings.");
      }
    } else {
      updateSettings({ notificationsEnabled: false });
      trackAppEvents.notificationsDisabled();
    }
  }, [settings.notificationsEnabled, updateSettings]);

  const showQuote = useCallback((type) => {
    const quotes = type === 'motivation' ? MOTIVATION_QUOTES : TRASH_TALK_QUOTES;
    const quoteType = type === 'motivation' ? 'motivation' : 'trash';
    const used = usedQuotes[quoteType];

    let availableQuotes = quotes.filter(q => !used.includes(q));
    if (availableQuotes.length === 0) {
      availableQuotes = quotes;
      setUsedQuotes(prev => ({ ...prev, [quoteType]: [] }));
    }

    const randomQuote = availableQuotes[Math.floor(Math.random() * availableQuotes.length)];
    setActiveMessage({ text: randomQuote, type });
    setUsedQuotes(prev => ({ ...prev, [quoteType]: [...prev[quoteType], randomQuote] }));
    trackMotivationEvents.quoteViewed(type);

    setTimeout(() => setActiveMessage({ text: '', type: null }), 8000);
  }, [usedQuotes]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      trackAppEvents.installed();
    }
  }, [deferredPrompt]);

  const handleStart = useCallback(() => {
    const protocol = PROTOCOLS.find(p => p.id === selectedProtocol);
    if (protocol) {
      startFast(protocol.id, protocol.hours);
      trackFastingEvents.start(protocol.id, protocol.hours);
    }
  }, [selectedProtocol, startFast]);

  const handleEndFast = useCallback(() => {
    const duration = elapsed / 3600;
    const protocol = PROTOCOLS.find(p => p.id === protocolId);
    trackFastingEvents.complete(duration, targetHours, protocol?.label || 'Unknown');
    endFast();
  }, [elapsed, targetHours, protocolId, endFast]);

  const handleCancelFast = useCallback(() => {
    trackFastingEvents.cancel(elapsed, targetHours);
    cancelFast();
  }, [elapsed, targetHours, cancelFast]);

  const handleShareCurrent = useCallback(() => {
    const protocol = PROTOCOLS.find(p => p.id === protocolId)?.label || 'Intermittent';
    const text = isFasting
      ? generateFastingText(elapsed / 3600, protocol)
      : generateGoalText(PROTOCOLS.find(p => p.id === selectedProtocol)?.label || 'New');
    shareProgress({
      title: isFasting ? 'My Fasting Progress' : 'New Fasting Goal',
      text
    });
  }, [isFasting, elapsed, protocolId, selectedProtocol]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    trackNavigation(tab);
  }, []);

  const handleToggleUnits = useCallback(() => {
    const newUnits = settings?.units === 'metric' ? 'imperial' : 'metric';
    toggleUnits();
    trackAppEvents.unitsChanged(newUnits);
  }, [settings?.units, toggleUnits]);

  const elapsedHours = useMemo(() => elapsed / 3600, [elapsed]);

  const formattedStartTime = useMemo(() => {
    if (!startTime) return null;
    try {
      return format(new Date(startTime), 'MMM d, h:mm a');
    } catch (e) {
      return null;
    }
  }, [startTime]);

  const earnedBadges = useMemo(() => {
    return BADGES.filter(badge => {
      if (badge.reqHours && longestFast >= badge.reqHours) return true;
      if (badge.reqStreak && streak >= badge.reqStreak) return true;
      return false;
    });
  }, [longestFast, streak]);

  // Show minimal loading spinner only if still loading
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
          <span className="font-bold text-slate-900 text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-32 vh-fix">
      <header className="fixed top-0 w-full z-30 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-md mx-auto px-6 h-20 flex items-center justify-between">
          <a
            href="https://ketomachine.sheker.com/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity active:scale-95"
          >
            <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiZap} className="text-white text-lg" />
            </div>
            <h1 className="text-xl font-black tracking-tighter text-slate-900 uppercase">KetoMachine</h1>
          </a>

          <div className="flex items-center gap-2">
            {activeTab === 'timer' && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handleShareCurrent}
                className="p-2.5 bg-slate-100 text-slate-600 rounded-xl border border-slate-200 shadow-sm"
              >
                <SafeIcon icon={FiShare2} className="text-sm" />
              </motion.button>
            )}

            {activeTab === 'timer' && !isFasting && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleStart}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.15em] shadow-lg shadow-blue-100 flex items-center gap-2 border-b-2 border-blue-800 transition-all active:translate-y-0.5 active:border-b-0"
              >
                <SafeIcon icon={FiPlay} className="text-xs" />
                Quick Start
              </motion.button>
            )}

            {(activeTab === 'body' || activeTab === 'stats') && (
              <button
                onClick={handleToggleUnits}
                className="px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 shadow-sm active:bg-slate-200 transition-colors"
              >
                {settings?.units === 'metric' ? 'Metric' : 'Imperial'}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="pt-24 px-6 max-w-md mx-auto">
        <AnimatePresence mode="wait">
          {activeTab === 'timer' && (
            <motion.div
              key="timer"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => showQuote('motivation')}
                    className="flex items-center justify-center gap-2 py-3 bg-emerald-50 text-emerald-600 rounded-2xl border border-emerald-100 font-black text-[10px] uppercase tracking-widest shadow-sm active:scale-95 transition-all"
                  >
                    <SafeIcon icon={FiHeart} className="text-sm" />
                    Inspiration
                  </button>
                  <button
                    onClick={() => showQuote('trash')}
                    className="flex items-center justify-center gap-2 py-3 bg-rose-50 text-rose-600 rounded-2xl border border-rose-100 font-black text-[10px] uppercase tracking-widest shadow-sm active:scale-95 transition-all"
                  >
                    <SafeIcon icon={FiFrown} className="text-sm" />
                    Trash Talk
                  </button>
                </div>

                <AnimatePresence>
                  {activeMessage.text && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: -5 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -5 }}
                      className={`p-5 rounded-2xl border text-center font-bold text-sm leading-relaxed shadow-lg ${
                        activeMessage.type === 'motivation'
                          ? 'bg-emerald-500 text-white border-emerald-400'
                          : 'bg-slate-900 text-white border-slate-800'
                      }`}
                    >
                      "{activeMessage.text}"
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isFasting ? (
                <>
                  {formattedStartTime && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-center gap-2 shadow-sm"
                    >
                      <SafeIcon icon={FiCalendar} className="text-slate-400 text-sm" />
                      <span className="text-xs font-bold text-slate-500">Started:</span>
                      <span className="text-xs font-black text-slate-900">{formattedStartTime}</span>
                    </motion.div>
                  )}

                  <TimerCircle elapsed={elapsed} targetHours={targetHours} isFasting={true} />
                  <MetabolicTracker elapsedHours={elapsedHours} />

                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <Button variant="primary" onClick={handleEndFast} className="flex-1">Complete</Button>
                    <Button variant="danger" onClick={handleCancelFast} className="flex-1">Cancel</Button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="text-center pt-2">
                    <h2 className="text-3xl font-black tracking-tighter mb-1">Build Metabolism.</h2>
                    <p className="text-slate-400 text-sm font-medium">Select a protocol to begin.</p>
                  </div>

                  <TimerCircle
                    elapsed={0}
                    targetHours={PROTOCOLS.find(p => p.id === selectedProtocol)?.hours || 16}
                    isFasting={false}
                  />

                  <ProtocolSelector selectedId={selectedProtocol} onSelect={setSelectedProtocol} />

                  <motion.div
                    className="pt-6 relative"
                    initial={{ scale: 1 }}
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                  >
                    <div className="absolute inset-0 bg-emerald-400/20 blur-2xl rounded-full scale-90 animate-pulse" />
                    <button
                      onClick={handleStart}
                      className="w-full py-7 text-2xl uppercase tracking-[0.25em] bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-[0_20px_50px_rgba(16,185,129,0.3)] border-b-8 border-emerald-700 active:border-b-0 active:translate-y-1 transition-all rounded-[2rem] font-black relative z-10 flex items-center justify-center gap-3"
                    >
                      <SafeIcon icon={FiPlay} className="text-2xl" />
                      Start Fast
                    </button>
                  </motion.div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'body' && (
            <motion.div
              key="body"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <BodyTracker
                weightLogs={weightLogs || []}
                measurements={measurements || []}
                photos={photos || []}
                onAddWeight={addWeight}
                onAddMeasurements={addMeasurements}
                onAddPhoto={addPhoto}
                units={settings?.units}
              />
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-black tracking-tighter">Analytics</h2>
                <ShareAchievements
                  streak={streak}
                  longestFast={longestFast}
                  totalFasts={history?.length || 0}
                  earnedBadges={earnedBadges}
                />
              </div>
              <StatsChart
                history={history || []}
                weightLogs={weightLogs || []}
                streak={streak || 0}
                longestFast={longestFast || 0}
                units={settings?.units}
              />
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-8"
            >
              <h2 className="text-3xl font-black tracking-tighter">Settings</h2>

              <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-2xl transition-colors ${
                      settings.notificationsEnabled ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'
                    }`}>
                      <SafeIcon icon={FiBell} className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-black text-lg text-slate-900 leading-tight">Reminders</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Phase & Goal Alerts</p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleNotifications}
                    className={`w-14 h-8 rounded-full relative transition-colors duration-300 ${
                      settings.notificationsEnabled ? 'bg-blue-500' : 'bg-slate-200'
                    }`}
                  >
                    <motion.div
                      animate={{ x: settings.notificationsEnabled ? 28 : 4 }}
                      className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-sm"
                    />
                  </button>
                </div>

                {settings.notificationsEnabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-4 pt-4 border-t border-slate-50"
                  >
                    <ReminderToggle
                      label="Metabolic Phase Changes"
                      active={settings.remindPhases}
                      onClick={() => updateSettings({ remindPhases: !settings.remindPhases })}
                    />
                    <ReminderToggle
                      label="Fasting Goal Reached"
                      active={settings.remindGoal}
                      onClick={() => updateSettings({ remindGoal: !settings.remindGoal })}
                    />
                  </motion.div>
                )}
              </div>

              <div className="bg-slate-900 rounded-[2.5rem] p-8 shadow-2xl text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-4 bg-white/10 text-white rounded-2xl">
                    <SafeIcon icon={FiDownload} className="text-2xl" />
                  </div>
                  <h3 className="font-black text-xl">Install App</h3>
                </div>
                <p className="text-slate-400 text-sm font-medium leading-relaxed mb-6">
                  Get the full experience with home screen access, faster loading, and offline tracking.
                </p>
                <Button
                  onClick={handleInstall}
                  className="w-full bg-white text-slate-900"
                  disabled={!deferredPrompt}
                >
                  {deferredPrompt ? 'Add to Home Screen' : 'Already Installed'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <nav className="fixed bottom-0 w-full z-40 px-6 pb-8">
        <div className="max-w-md mx-auto bg-white/90 backdrop-blur-xl border border-slate-100 shadow-2xl rounded-[2.5rem] flex justify-around p-2">
          <NavButton active={activeTab === 'timer'} onClick={() => handleTabChange('timer')} icon={FiClock} label="Timer" />
          <NavButton active={activeTab === 'body'} onClick={() => handleTabChange('body')} icon={FiActivity} label="Body" />
          <NavButton active={activeTab === 'stats'} onClick={() => handleTabChange('stats')} icon={FiBarChart2} label="Stats" />
          <NavButton active={activeTab === 'settings'} onClick={() => handleTabChange('settings')} icon={FiSettings} label="Settings" />
        </div>
      </nav>
    </div>
  );
}

const ReminderToggle = React.memo(({ label, active, onClick }) => (
  <div onClick={onClick} className="flex items-center justify-between cursor-pointer group">
    <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900 transition-colors">{label}</span>
    <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
      active ? 'bg-blue-500 border-blue-500' : 'bg-white border-slate-200'
    }`}>
      {active && <SafeIcon icon={FiCheck} className="text-white text-xs" />}
    </div>
  </div>
));

const NavButton = React.memo(({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center flex-col justify-center gap-1 px-5 py-3 rounded-[1.5rem] transition-all duration-300 ${
      active ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'text-slate-400 hover:text-slate-900'
    }`}
  >
    <Icon className={`text-xl ${active ? 'scale-110' : ''}`} />
    <span className="text-[8px] font-black uppercase tracking-[0.1em]">{label}</span>
  </button>
));

export default App;